import { useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/hooks/use-auth'
import { type AdminUserSort } from '@/lib/api'
import { apiV2, ApiV2Error } from '@/lib/api-v2-client'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react'
import { toast } from 'sonner'
import { useDebouncedValue } from '@/hooks/use-debounced-value'

const PER_PAGE = 25

type SortKey = AdminUserSort

type SortColumn = 'name' | 'email' | 'role' | 'signed_up' | 'last_login'

const COLUMN_SORTS: Record<SortColumn, [SortKey, SortKey]> = {
  name: ['name_asc', 'name_desc'],
  email: ['email_asc', 'email_desc'],
  role: ['role_asc', 'role_desc'],
  signed_up: ['signed_up_desc', 'signed_up_asc'],
  last_login: ['last_login_desc', 'last_login_asc'],
}

export const Route = createFileRoute('/_authenticated/admin')({
  component: AdminPage,
})

export function AdminPage() {
  const { user, refreshAuth } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<SortKey>('signed_up_desc')
  const debouncedQuery = useDebouncedValue(query, 300)

  const { data: usersData, isLoading } = useQuery({
    queryKey: ['admin-users', debouncedQuery, page, sort],
    queryFn: () => apiV2.adminSearchUsers(debouncedQuery, page, PER_PAGE, sort),
    enabled: !!user?.is_admin,
  })

  const impersonateMutation = useMutation({
    mutationFn: (userId: string) => apiV2.startImpersonation(userId),
    onSuccess: async () => {
      await refreshAuth()
      queryClient.clear()
      toast.success('Impersonation started')
      navigate({ to: '/dashboard' })
    },
    onError: (error) => {
      if (error instanceof ApiV2Error) {
        const msg: Record<string, string> = {
          cannot_impersonate_self: 'Cannot impersonate yourself',
          cannot_impersonate_admin: 'Cannot impersonate another admin',
          already_impersonating: 'Already impersonating a user — exit first',
          session_auth_required: 'Must be logged in via session (not API key)',
          user_not_found: 'User not found',
        }
        toast.error(msg[error.message] ?? error.message)
      }
    },
  })

  const users = usersData?.data ?? []
  const total = usersData?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE))

  const handleHeaderClick = (column: SortColumn) => {
    const [primary, secondary] = COLUMN_SORTS[column]
    setSort(sort === primary ? secondary : primary)
    setPage(1)
  }

  const renderSortIndicator = (column: SortColumn) => {
    const [primary, secondary] = COLUMN_SORTS[column]
    if (sort === primary) return <ArrowDown className="h-3.5 w-3.5 ml-1 inline" />
    if (sort === secondary) return <ArrowUp className="h-3.5 w-3.5 ml-1 inline" />
    return <ArrowUpDown className="h-3.5 w-3.5 ml-1 inline opacity-30" />
  }

  // Guard after hooks so their call order remains stable between auth states.
  if (!user?.is_admin) {
    navigate({ to: '/dashboard' })
    return null
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold">Admin Panel</h1>
          <p className="text-muted-foreground">Search and impersonate users for support purposes. All actions are audited.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild>
            <Link to="/admin/notifications">Notifications</Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/admin/nonprofits">Nonprofits</Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <CardDescription>Search by name or email across all organizations</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              type="search"
              aria-label="Search users by name or email"
              placeholder="Search by name or email…"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(1) }}
              autoFocus
              className="sm:flex-1"
            />
            <Select value={sort} onValueChange={(v) => { setSort(v as SortKey); setPage(1) }}>
              <SelectTrigger className="sm:w-56" aria-label="Sort users">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="signed_up_desc">Most recent signups</SelectItem>
                <SelectItem value="signed_up_asc">Oldest signups</SelectItem>
                <SelectItem value="last_login_desc">Most recent login</SelectItem>
                <SelectItem value="last_login_asc">Oldest login</SelectItem>
                <SelectItem value="email_asc">Email (A→Z)</SelectItem>
                <SelectItem value="email_desc">Email (Z→A)</SelectItem>
                <SelectItem value="name_asc">Name (A→Z)</SelectItem>
                <SelectItem value="name_desc">Name (Z→A)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead
                    className="cursor-pointer select-none hover:text-foreground"
                    onClick={() => handleHeaderClick('name')}
                  >
                    Name{renderSortIndicator('name')}
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none hover:text-foreground"
                    onClick={() => handleHeaderClick('email')}
                  >
                    Email{renderSortIndicator('email')}
                  </TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead
                    className="cursor-pointer select-none hover:text-foreground"
                    onClick={() => handleHeaderClick('role')}
                  >
                    Role{renderSortIndicator('role')}
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none hover:text-foreground"
                    onClick={() => handleHeaderClick('signed_up')}
                  >
                    Signed up{renderSortIndicator('signed_up')}
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none hover:text-foreground"
                    onClick={() => handleHeaderClick('last_login')}
                  >
                    Last login{renderSortIndicator('last_login')}
                  </TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">
                      Loading…
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">
                      No users found.
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-muted-foreground">{u.email}</TableCell>
                    <TableCell className="text-muted-foreground">{u.organization_name}</TableCell>
                    <TableCell className="text-muted-foreground">{u.role}</TableCell>
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {formatDate(u.inserted_at)}
                    </TableCell>
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {u.last_signed_in_at ? formatDate(u.last_signed_in_at) : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={impersonateMutation.isPending || !!u.is_admin}
                        onClick={() => impersonateMutation.mutate(u.id)}
                      >
                        {u.is_admin ? 'Admin' : 'Impersonate'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {total > 0 && (
            <div className="flex items-center justify-between border-t pt-4">
              <p className="text-sm text-muted-foreground">
                Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)} of {total}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function formatDate(iso: string) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}
